import { replace } from "connected-react-router";
import { omit } from "lodash";
import { SagaIterator } from "redux-saga";
import { all, call, put, takeEvery } from "redux-saga/effects";

import { ToastContent, getToastContent } from "constants/toasts";
import { SettingsRoutes } from "routes/settings/constants";
import { DEFAULT_SETTINGS } from "routes/settings/contact-form/constants";
import { ApiManager } from "services/api";
import { ContactFormSerializer } from "services/api/contact-forms";
import { ToastVariant, ToastsActions } from "store/features/toasts";
import { IActionWithPayload } from "store/helper";

import {
  CRUDAction,
  IEntityCreate,
  IEntityCreateFailure,
  IEntityDelete,
  IEntityDeleteSuccess,
  IEntityFetch,
  IEntityUpdate,
  IEntityUpdateFailure,
  RequestAction,
} from "../actions";

import { ContactFormActionNames, ContactFormActions } from "./actions";
import { IContactForm } from "./interfaces";

function* fetch(action: IActionWithPayload<string, IEntityFetch>): SagaIterator {
  const { id } = action.payload;
  const response = yield call(ApiManager.contactFormApi.fetch, id);

  if (response.result) {
    yield put(ContactFormActions.fetchSuccess({ id, value: ContactFormSerializer.deserialize(response.result) }));
  } else {
    yield put(ContactFormActions.fetchFailure({ id, error: response.error }));
  }
}

function* fetchCollection(): SagaIterator {
  const response = yield call(ApiManager.contactFormApi.fetchAll);

  if (response.result) {
    yield put(
      ContactFormActions.fetchCollectionSuccess({ values: response.result.map(ContactFormSerializer.deserialize) })
    );
  } else {
    yield put(ContactFormActions.fetchCollectionFailure({ error: response.error }));
  }
}

function* update(action: IActionWithPayload<string, IEntityUpdate<IContactForm>>): SagaIterator {
  const {
    value,
    value: { id },
  } = action.payload;

  if (!id) {
    return;
  }

  const response = yield call(ApiManager.contactFormApi.update, id, omit(value, "updatedAt"));

  if (response.result) {
    yield put(ContactFormActions.updateSuccess({ id, value: ContactFormSerializer.deserialize(response.result) }));
  } else {
    yield put(
      ContactFormActions.updateFailure({ id, errorObject: response.error.error, error: response.error.error.message })
    );
  }
}

function* updateDefault(action: IActionWithPayload<string, IEntityUpdate<IContactForm>>): SagaIterator {
  const { value } = action.payload;

  const form = { ...value };

  if (form.consentMessage?.length === 0) {
    form.consentMessage = DEFAULT_SETTINGS.consentMessage;
  }

  const response = yield call(ApiManager.licenseApi.updateDefaultContactForm, form);

  if (response.result) {
    yield put(ContactFormActions.updateDefaultSuccess());
  } else {
    yield put(ContactFormActions.updateDefaultFailure());
  }
}

function* create(action: IActionWithPayload<string, IEntityCreate<IContactForm>>): SagaIterator {
  const { value } = action.payload;

  const response = yield call(ApiManager.contactFormApi.create, value);

  if (response.result) {
    if (response.result.version === 2) {
      yield put(replace(`${SettingsRoutes.ContactForms}/edit/${response.result.ID}`));
    }
    yield put(
      ContactFormActions.createSuccess({
        id: response.result.ID,
        value: ContactFormSerializer.deserialize(response.result),
      })
    );
  } else {
    yield put(
      ContactFormActions.createFailure({ errorObject: response.error.error, error: response.error.error.message })
    );
  }
}

function* destroy(action: IActionWithPayload<string, IEntityDelete & { skipToast?: boolean }>): SagaIterator {
  const { id, skipToast = false } = action.payload;

  if (!id) {
    return;
  }
  const response = yield call(ApiManager.contactFormApi.destroy, id);

  if (response.result) {
    yield put(ContactFormActions.deleteSuccess({ id, skipToast }));
  } else {
    yield put(ContactFormActions.deleteFailure({ id }));
  }
}

function* destroySuccess(
  action: IActionWithPayload<string, IEntityDeleteSuccess & { skipToast?: boolean }>
): SagaIterator {
  const { skipToast = false } = action.payload;

  if (!skipToast) {
    yield put(
      ToastsActions.createToast({
        variant: ToastVariant.Success,
        content: getToastContent(ToastContent.DELETE_CONTACT_FORM_SUCCESS),
      })
    );
  }
}

function* destroyFailure(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      variant: ToastVariant.Error,
      content: getToastContent(ToastContent.DELETE_CONTACT_FORM_FAILURE),
    })
  );
}

function* updateSuccess(): SagaIterator {
  yield put(replace(SettingsRoutes.ContactForms));
  yield put(
    ToastsActions.createToast({
      variant: ToastVariant.Success,
      content: getToastContent(ToastContent.UPDATE_CONTACT_FORM_SUCCESS),
    })
  );
}

function* updateFailure(action: IActionWithPayload<string, IEntityUpdateFailure>): SagaIterator {
  yield put(
    ToastsActions.createToast({
      variant: ToastVariant.Error,
      content: getToastContent(ToastContent.UPDATE_CONTACT_FORM_FAILURE, { type: action?.payload?.errorObject!.type }),
    })
  );
}

function* createSuccess(): SagaIterator {
  yield put(
    ToastsActions.createToast({
      variant: ToastVariant.Success,
      content: getToastContent(ToastContent.CREATE_CONTACT_FORM_SUCCESS),
    })
  );
}

function* createFailure(action: IActionWithPayload<string, IEntityCreateFailure>): SagaIterator {
  yield put(
    ToastsActions.createToast({
      variant: ToastVariant.Error,
      content: getToastContent(ToastContent.CREATE_CONTACT_FORM_FAILURE, { type: action.payload.errorObject!.type }),
    })
  );
}

export function* contactFormsSaga(): SagaIterator {
  yield all([
    takeEvery(ContactFormActionNames[CRUDAction.FETCH_SINGLE][RequestAction.REQUEST], fetch),
    takeEvery(ContactFormActionNames[CRUDAction.FETCH_COLLECTION][RequestAction.REQUEST], fetchCollection),
    takeEvery(ContactFormActionNames[CRUDAction.UPDATE][RequestAction.REQUEST], update),
    takeEvery(ContactFormActionNames[CRUDAction.DELETE][RequestAction.REQUEST], destroy),
    takeEvery(ContactFormActionNames[CRUDAction.DELETE][RequestAction.SUCCESS], destroySuccess),
    takeEvery(ContactFormActionNames[CRUDAction.DELETE][RequestAction.FAILURE], destroyFailure),
    takeEvery(
      [
        ContactFormActionNames[CRUDAction.UPDATE][RequestAction.SUCCESS],
        ContactFormActionNames.UPDATE_DEFAULT[RequestAction.SUCCESS],
      ],
      updateSuccess
    ),
    takeEvery(ContactFormActionNames.UPDATE_DEFAULT[RequestAction.REQUEST], updateDefault),
    takeEvery(
      [
        ContactFormActionNames[CRUDAction.UPDATE][RequestAction.FAILURE],
        ContactFormActionNames.UPDATE_DEFAULT[RequestAction.FAILURE],
      ],
      updateFailure
    ),
    takeEvery(ContactFormActionNames[CRUDAction.CREATE][RequestAction.SUCCESS], createSuccess),
    takeEvery(ContactFormActionNames[CRUDAction.CREATE][RequestAction.FAILURE], createFailure),
    takeEvery(ContactFormActionNames[CRUDAction.CREATE][RequestAction.REQUEST], create),
  ]);
}
